import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { z } from 'zod'
import {
  ArrowRight,
  AtSign,
  CheckSquare,
  Inbox,
  Loader2,
} from 'lucide-react'
import { api } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { FilterRow } from '../../components/filters/FilterRow'

/** Response shape — mirrors `/me/replies` minus the `mentionsMe` flag,
 *  which is implicitly true for every row in this feed. */
const assignedCommentRow = z.object({
  id: z.string(),
  taskId: z.string(),
  taskName: z.string(),
  spaceId: z.string().nullable(),
  spaceName: z.string().nullable(),
  listId: z.string().nullable(),
  listName: z.string().nullable(),
  authorId: z.string(),
  authorFullName: z.string(),
  authorAvatarUrl: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
})
const assignedCommentsResponse = z.object({
  data: z.array(assignedCommentRow),
  nextCursor: z
    .object({
      cursorCreatedAt: z.string(),
      cursorId: z.string(),
    })
    .nullable(),
})

/** Same relative formatter the Replies page uses — keeps the two
 *  comment-driven feeds tonally identical so the user reads them as
 *  siblings. */
function fmtRelative(iso) {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  return formatDateTime(iso)
}

function initials(name) {
  const parts = String(name ?? '').trim().split(/\s+/)
  if (parts.length === 0 || parts[0] === '') return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Strip @[uuid] tokens out of the comment body for the preview line.
 *  We keep them in the database so the task detail can render mention
 *  chips, but list rows look cleaner without raw markup. */
function previewBody(raw) {
  return String(raw ?? '')
    .replace(/@\[[0-9a-f-]{36}\]/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function linkForComment(c) {
  if (c.spaceId && c.listId) {
    return `/app/spaces/${c.spaceId}/lists/${c.listId}`
  }
  return '/app/focus-board'
}

/** Single action-item row. Visually heavier than a Reply row — a
 *  left-edge accent bar + a "CheckSquare" task badge in the header
 *  make clear this is something to act on, not just read. */
function AssignedCommentRow({ comment }) {
  return (
    <Link
      to={linkForComment(comment)}
      className="group relative block rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 pl-5 hover:border-[color:var(--color-primary)]/40 hover:shadow-sm transition-all cursor-pointer overflow-hidden"
    >
      {/* Accent stripe down the left edge — same primary blue as the
          "Mention" chip so the action-item framing reads at a glance. */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1 bg-[color:var(--color-primary)]"
      />

      <div className="flex items-start gap-3">
        {comment.authorAvatarUrl ? (
          <img
            src={comment.authorAvatarUrl}
            alt={comment.authorFullName}
            className="h-9 w-9 rounded-full object-cover shrink-0 mt-0.5"
          />
        ) : (
          <span className="h-9 w-9 rounded-full shrink-0 mt-0.5 flex items-center justify-center bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)] font-semibold text-xs">
            {initials(comment.authorFullName)}
          </span>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[color:var(--color-ink-strong)] text-sm">
              {comment.authorFullName}
            </span>
            <span className="text-xs text-[color:var(--color-ink-muted)]">
              tagged you on
            </span>
            <span className="font-medium text-[color:var(--color-ink)] text-sm truncate max-w-[28ch]">
              {comment.taskName}
            </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)]">
              <AtSign size={10} />
              Mention
            </span>
            <span
              className="ml-auto text-xs text-[color:var(--color-ink-muted)] shrink-0"
              title={formatDateTime(comment.createdAt)}
            >
              {fmtRelative(comment.createdAt)}
            </span>
          </div>

          <p className="mt-1.5 text-sm text-[color:var(--color-ink)] line-clamp-3 whitespace-pre-wrap">
            {previewBody(comment.body)}
          </p>

          {comment.spaceName || comment.listName ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
              {comment.spaceName ? <span>{comment.spaceName}</span> : null}
              {comment.spaceName && comment.listName ? (
                <ArrowRight size={10} aria-hidden="true" />
              ) : null}
              {comment.listName ? <span>{comment.listName}</span> : null}
              <span className="ml-auto text-[color:var(--color-primary)] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 font-medium">
                Open task
                <ArrowRight size={12} />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  )
}

export function AssignedCommentsPage() {
  const commentsQuery = useInfiniteQuery({
    queryKey: ['me', 'assigned-comments'],
    queryFn: async ({ pageParam }) => {
      const params = { limit: 20 }
      if (pageParam) {
        params.cursorCreatedAt = pageParam.cursorCreatedAt
        params.cursorId = pageParam.cursorId
      }
      const res = await api.get('/me/assigned-comments', { params })
      return assignedCommentsResponse.parse(res.data)
    },
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  })

  const allComments = useMemo(() => {
    const pages = commentsQuery.data?.pages ?? []
    return pages.flatMap((p) => p.data)
  }, [commentsQuery.data])

  // Free-text search across task name, space, list, author and the
  // comment body itself. Client-side over what's loaded — the feed
  // is infinite-scroll so the user can pull more pages and the filter
  // re-applies as new rows arrive. The "showing N+ of M" count above
  // still reflects what the user is actually looking at.
  const [search, setSearch] = useState('')
  const filteredComments = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allComments
    return allComments.filter((c) => {
      const hay =
        `${c.taskName ?? ''} ${c.spaceName ?? ''} ${c.listName ?? ''} ${c.authorFullName ?? ''} ${c.body ?? ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [allComments, search])

  // Total count for the header. We only count what's loaded — if the
  // user scrolls past page 1, the number ticks up. Good enough for a
  // "you have N things waiting" hint without a separate count query.
  const visibleCount = filteredComments.length
  const hasMore = Boolean(commentsQuery.hasNextPage)

  return (
    <div>
      <Breadcrumb
        current="Assigned Comments"
        homePath="/app"
        homeLabel="Dashboard"
      />

      <div className="flex items-baseline justify-between gap-3 mt-2 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] leading-tight">
            Assigned Comments
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Comments where someone tagged you.
            {visibleCount > 0 ? (
              <>
                {' '}
                <span className="font-medium text-[color:var(--color-ink-strong)]">
                  {visibleCount}
                  {hasMore ? '+' : ''}{' '}
                  open
                </span>
                .
              </>
            ) : null}
          </p>
        </div>
      </div>

      {allComments.length > 0 ? (
        <FilterRow>
          <FilterRow.Search
            value={search}
            onChange={setSearch}
            placeholder="Search by Task Name, Author or Comment Body"
          />
        </FilterRow>
      ) : null}

      {commentsQuery.isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--color-ink-muted)] text-sm">
          <Loader2 size={18} className="animate-spin mr-2" />
          Loading your action items…
        </div>
      ) : null}

      {commentsQuery.isError ? (
        <div className="rounded-xl border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 p-4 text-sm text-[color:var(--color-priority-urgent)]">
          We couldn't load your assigned comments. Please refresh and
          try again.
        </div>
      ) : null}

      {!commentsQuery.isLoading && filteredComments.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-primary)] mb-3">
            <Inbox size={22} />
          </div>
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] mb-1">
            Nothing Assigned to You
          </h2>
          <p className="text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            When a teammate tags you with{' '}
            <span className="font-mono text-[color:var(--color-ink)] text-xs px-1 py-0.5 rounded bg-[color:var(--color-rule)]/30">
              @YourName
            </span>{' '}
            inside a task comment, the comment lands here so you can
            act on it without hunting through every task.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)]">
            <CheckSquare size={12} />
            Comments addressed to you become action items.
          </div>
        </div>
      ) : null}

      {allComments.length > 0 ? (
        <div className="space-y-2">
          {filteredComments.map((c) => (
            <AssignedCommentRow key={c.id} comment={c} />
          ))}

          {commentsQuery.hasNextPage ? (
            <div className="pt-2 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => commentsQuery.fetchNextPage()}
                disabled={commentsQuery.isFetchingNextPage}
              >
                {commentsQuery.isFetchingNextPage ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>Load More</>
                )}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
